<script setup lang="ts">
import type { OrthancStudyInfo } from '@/src/core/orthanc-api';

import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import { useOrthancStore } from '../store/dicom-web/orthanc-store';
import {
  STUDY_SEARCH_FILTER_PROPERTIES,
  useStudiesStore,
} from '../store/study-list-home';
import LoadingScreen from './LoadingScreen.vue';
import OrthancStatistics from './OrthancStatistics.vue';

import StudyElement from './StudyElement.vue';

const props = defineProps({
  targetStudy: String,
});

const emit = defineEmits(['studyClicked', 'studyDeleted']);

type TaskStatus = 'pending' | 'done' | 'failed' | 'timeout';

const SORT_PROPERTIES: Record<
  string,
  { displayText: string; isDate?: boolean }
> = {
  PatientName: { displayText: 'Name' },
  PatientID: { displayText: 'Patient ID' },
  PatientSex: { displayText: 'Sex' },
  PatientBirthDate: { displayText: 'DOB', isDate: true },
  StudyDate: { displayText: 'Study Date', isDate: true },
  AccessionNumber: { displayText: 'Accession No.' },
  StudyDescription: { displayText: 'Description' },
  Modality: { displayText: 'Modality' },
  InstitutionName: { displayText: 'Institution' },
  ReferringPhysicianName: { displayText: 'Ref. Physician' },
  Manufacturer: { displayText: 'Manufacturer' },
  ManufacturerModelName: { displayText: 'Model' },
  MagneticFieldStrength: { displayText: 'Field Strength' },
  SoftwareVersions: { displayText: 'Software' },
  LastUpdate: { displayText: 'Import Date' },
};

function parseOrthancDate(v?: string): number {
  const m = v?.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/);
  if (!m) return 0;
  return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
}

const loaded = ref(false);
const sortOrder = ref(true);
// Local clock tick so time-based task timeouts re-evaluate even when the
// server data (and thus the study poll) reports no change. This is a pure
// in-memory timer — it does NOT trigger any network polling.
const now = ref(Date.now());
let clockTimer: ReturnType<typeof setInterval> | undefined;
// const sortByProperties = ref(SORT_PROPERTIES);

const orthancStore = useOrthancStore();
const studiesStore = useStudiesStore();

function flattenStudy(s: OrthancStudyInfo) {
  const tags = s.MainDicomTags;
  const ptags = s.PatientMainDicomTags;
  const patientName = ptags?.PatientName || 'Unknown';

  // todo review
  const TASK_TIMEOUT_MS = 5 * 60 * 1000;
  const lastUpdateStr = s.LastUpdate ?? s.Metadata?.LastUpdate ?? '';
  const lastMs = parseOrthancDate(lastUpdateStr);
  // Depends on the reactive `now` tick so a stuck (never-updated) task can
  // still transition to `timeout` in the UI without a server-side change.
  const timedOut = lastMs > 0 && now.value - lastMs > TASK_TIMEOUT_MS;
  const taskStatus = (key: string): TaskStatus | null => {
    const raw = s.Metadata?.[key];
    if (!raw) return null;
    if (raw === 'done') return 'done';
    if (raw === 'failed') return 'failed';
    if (raw === 'pending' || raw === 'calculating') {
      return timedOut ? 'timeout' : 'pending';
    }
    return null;
  };

  const segmentationStatus = taskStatus('OCG19_SegmentationStatus');
  const metadataStatus = taskStatus('OCG19_MetadataStatus');

  return {
    StudyInstanceUID: tags.StudyInstanceUID,
    OrthancID: s.ID,
    PatientName: patientName,
    PatientID: ptags?.PatientID || tags.StudyInstanceUID,
    PatientSex: ptags?.PatientSex || '',
    PatientBirthDate: ptags?.PatientBirthDate || '',
    PatientWeight: ptags?.PatientWeight || '',
    PatientSize: ptags?.PatientSize || '',
    StudyDate: tags.StudyDate || '',
    StudyTime: tags.StudyTime || '',
    AccessionNumber: tags.AccessionNumber || '',
    StudyDescription: tags.StudyDescription || '',
    SeriesCount: s.Series.length,
    Modality: tags.Modality || '',
    InstitutionName: tags.InstitutionName || '',
    ReferringPhysicianName: tags.ReferringPhysicianName || '',
    Manufacturer: tags.Manufacturer || '',
    ManufacturerModelName: tags.ManufacturerModelName || '',
    MagneticFieldStrength: tags.MagneticFieldStrength || '',
    SoftwareVersions: tags.SoftwareVersions || '',
    LastUpdate: lastUpdateStr,
    IsStable: s.IsStable || false,
    segmentationStatus,
    segmentationPending: segmentationStatus === 'pending',
    metadataStatus,
    metadataPending: metadataStatus === 'pending',
  };
}

const studies = computed(() => {
  return orthancStore.studies.map(flattenStudy);
});

const selectedStudy = computed(() => {
  return (
    studies.value.find(
      study => study.StudyInstanceUID === props.targetStudy,
    ) || null
  );
});

const studySortBy = computed(() => studiesStore.getStudySortProperty);
const studySearchTerm = computed(() => studiesStore.getStudySearchTerm);

// const sortByDescriptors = computed(
//   () => studiesStore.getAllPropertyDisplayText
// );
// const sortByActiveDescriptors = computed(
//   () => studiesStore.getActivePropertyDescriptors
// );

const filteredStudies = computed(() => {
  const filterString = studySearchTerm.value.toLowerCase().replace(/\s/g, '');
  const filterFields = STUDY_SEARCH_FILTER_PROPERTIES;

  return studies.value.filter((study) => {
    return filterFields.some((field) => {
      const fieldValue = (study[field] || '').toLowerCase();
      return (
        fieldValue.includes(filterString)
        && study.StudyInstanceUID !== props.targetStudy
      );
    });
  });
});

const filteredSortedStudies = computed(() => {
  return [...filteredStudies.value].sort((a: any, b: any) => {
    const aValue = (a[studySortBy.value] || '').toString().toLowerCase();
    const bValue = (b[studySortBy.value] || '').toString().toLowerCase();

    if (sortOrder.value) {
      if (aValue > bValue) return -1;
      if (aValue < bValue) return 1;
    }
    if (aValue < bValue) return -1;
    if (aValue > bValue) return 1;
    return 0;
  });
});

const sortOptions = computed(() =>
  Object.entries(SORT_PROPERTIES).map(([key, prop]) => ({
    key,
    displayText: prop.displayText,
  })),
);

function setSort(tag: string) {
  if (studySortBy.value === tag) {
    sortOrder.value = !sortOrder.value;
  }
  studiesStore.setStudySortProperty(tag);
}

function handleStudyClicked(study: any) {
  emit('studyClicked', study);
}

function handleStudyDeleted(study: any) {
  emit('studyDeleted', study);
}

// function handleSelectionChange(clickedArray: string[]) {
//   sortByProperties.value =
//     studiesStore.setStudySortAvailablePropertiesByDescriptor(clickedArray);
// }

onMounted(async () => {
  try {
    await orthancStore.fetchStudies();
    orthancStore.startStudyPolling(10000);
  }
  catch (e) {
    console.error('Failed to fetch studies:', e);
  }
  finally {
    loaded.value = true;
  }
  clockTimer = setInterval(() => {
    now.value = Date.now();
  }, 30000);
});

onBeforeUnmount(() => {
  orthancStore.stopStudyPolling();
  if (clockTimer) clearInterval(clockTimer);
});
</script>

<template>
  <div class="flex flex-col overflow-hidden h-full">
    <div>
      <div class="flex justify-end">
        <OrthancStatistics />
      </div>
      <div class="flex align-center gap-2 mb-2">
        <select
          class="sort-select"
          aria-label="Sort studies by"
          :value="studySortBy"
          @change="setSort(($event.target as HTMLSelectElement).value)"
        >
          <option v-for="opt in sortOptions" :key="opt.key" :value="opt.key">
            {{ opt.displayText }}
          </option>
        </select>
        <button class="sort-order-btn" @click="sortOrder = !sortOrder">
          <i
            class="fa-solid fa-xs" :class="[
              sortOrder ? 'fa-arrow-up' : 'fa-arrow-down',
            ]"
          />
        </button>
      </div>
    </div>
    <div v-if="selectedStudy">
      <StudyElement
        :study="selectedStudy"
        :is-open="true"
        @study-clicked="handleStudyClicked"
        @study-deleted="handleStudyDeleted"
      />
      <div class="default-separator" />
    </div>
    <Transition>
      <div
        v-if="loaded"
        class="patient-container flex flex-col flex-grow min-h-0 overflow-y-auto gap-2"
      >
        <StudyElement
          v-for="(study, index) in filteredSortedStudies"
          :key="study.StudyInstanceUID"
          :study="study"
          :index="index"
          :is-open="targetStudy === study.StudyInstanceUID"
          @study-clicked="handleStudyClicked"
          @study-deleted="handleStudyDeleted"
        />
      </div>
      <LoadingScreen v-else />
    </Transition>
  </div>
</template>

<style scoped>
.patient-container {
  height: 100%;
}
.patient-container::-webkit-scrollbar {
  display: none;
  width: 0 !important;
}

.active {
  background: var(--md-primary);
  transition: all 0.3s ease;
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.3s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.sort-select {
  background: var(--md-bg);
  color: var(--md-text-primary);
  border: 1px solid var(--md-border);
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 0.75rem;
  height: 28px;
  cursor: pointer;
  outline: none;
}

.sort-select:hover {
  border-color: var(--md-primary);
}

.sort-order-btn {
  background: var(--md-bg);
  color: var(--md-text-primary);
  border: 1px solid var(--md-border);
  border-radius: 6px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.sort-order-btn:hover {
  border-color: var(--md-primary);
  background: var(--brand-gradient-hover);
}
</style>
